import React, { FC } from 'react';
import { Input } from 'antd';
import { Button } from 'shared/ui/Button';
import { ConfirmModal } from 'shared/ui/ConfirmModal';
import { Modal } from 'shared/ui/Modal';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useManageVerificationPhoto } from '../utils/hooks/useManageVerificationPhoto';
import { useScreenBreakpoints } from 'shared/utils/hooks/useScreenBreakpoints';

interface Props {
  verificationPhotoId: string;
  fileStatus?: 'pending' | 'approved' | 'rejected';
  onSuccess?: () => void;
}

export const ManageVerificationButtons: FC<Props> = ({ verificationPhotoId, fileStatus, onSuccess }) => {
  const {
    isLoading,
    isConfirmOpen,
    isRejectModalOpen,
    rejectReason,
    setRejectReason,
    openConfirmModal,
    closeConfirmModal,
    openRejectModal,
    closeRejectModal,
    handleApprove,
    handleReject,
  } = useManageVerificationPhoto({ verificationPhotoId, onSuccess });

  const { isMobile } = useScreenBreakpoints();
  const { t } = useAppTranslation(['profile', 'common']);

  const isApproved = fileStatus === 'approved';

  return (
    <div className={`flex flex-col w-full items-center ${isMobile ? 'gap-2 mt-3' : 'gap-3 mt-4'}`}>
      {!isApproved && (
        <Button
          size={isMobile ? 'small' : 'medium'}
          className="bg-green-500 text-white hover:bg-green-600 w-full flex items-center justify-center gap-2 py-2"
          onClick={openConfirmModal}
        >
          {t('Approve', { ns: 'common' })}
        </Button>
      )}

      <Button
        size={isMobile ? 'small' : 'medium'}
        className="bg-red-500 text-white hover:bg-red-600 w-full flex items-center justify-center gap-2 py-2"
        onClick={openRejectModal}
      >
        {t('Reject', { ns: 'common' })}
      </Button>

      <ConfirmModal
        isOpened={isConfirmOpen}
        title={t('Document approvement')}
        description={t('Are you sure you want to approve document?')}
        isLoading={isLoading}
        onOk={handleApprove}
        onCancel={closeConfirmModal}
      />

      <Modal isOpen={isRejectModalOpen} onClose={closeRejectModal}>
        <div className="space-y-4">
          <div className="flex flex-col items-center justify-center gap-4 mb-4">
            <div className="text-primary text-center font-semibold">{t('Specify rejection reason')}</div>
          </div>
          <Input.TextArea
            rows={4}
            placeholder={t('Enter rejection reason...')}
            value={rejectReason}
            onChange={(e) => {
              setRejectReason(e.target.value);
            }}
          />
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={closeRejectModal}>{t('Cancel', { ns: 'common' })}</Button>
            <Button onClick={handleReject} isLoading={isLoading} isDisabled={!rejectReason.trim()}>
              {t('Confirm', { ns: 'common' })}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
